import { lazy, Suspense, useEffect, useState } from 'react';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';
import { Shell } from './shell/Shell';
import { useData } from './data/stores';
import { FeedSimulator } from './domain';
import { Skeleton } from './components';
import { MapView } from './map/MapView';
import { NationalCOP } from './views/dashboard/NationalCOP';
import { AssetsView } from './views/assets/AssetsView';
import { AssetDetail } from './views/assets/AssetDetail';
import { AlertsView } from './views/alerts/AlertsView';
import { AlertDetail } from './views/alerts/AlertDetail';
import { ScenariosView } from './views/scenarios/ScenariosView';
import { OTMonitoringView } from './views/ot/OTMonitoringView';
import { KnowledgeGraphView } from './views/intel/KnowledgeGraphView';
import { ComplianceView } from './views/compliance/ComplianceView';
import { SettingsView } from './views/settings/SettingsView';

// CesiumJS pulls in a lot of WebGL machinery. Lazy-load it so the rest of the
// app stays snappy when the user never opens the 3D view.
const GlobeView = lazy(() =>
  import('./views/globe/GlobeView').then((m) => ({ default: m.GlobeView })),
);

const router = createBrowserRouter(
  [
  {
    path: '/',
    element: (
      <Shell>
        <NationalCOP />
      </Shell>
    ),
  },
  {
    path: '/map',
    element: (
      <Shell>
        <MapView />
      </Shell>
    ),
  },
  {
    path: '/globe',
    element: (
      <Shell>
        <Suspense fallback={<GlobeFallback />}>
          <GlobeView />
        </Suspense>
      </Shell>
    ),
  },
  {
    path: '/assets',
    element: (
      <Shell>
        <AssetsView />
      </Shell>
    ),
  },
  {
    path: '/assets/:id',
    element: (
      <Shell>
        <AssetDetail />
      </Shell>
    ),
  },
  {
    path: '/alerts',
    element: (
      <Shell>
        <AlertsView />
      </Shell>
    ),
  },
  {
    path: '/alerts/:id',
    element: (
      <Shell>
        <AlertDetail />
      </Shell>
    ),
  },
  {
    path: '/scenarios',
    element: (
      <Shell>
        <ScenariosView />
      </Shell>
    ),
  },
  {
    path: '/ot',
    element: (
      <Shell>
        <OTMonitoringView />
      </Shell>
    ),
  },
  {
    path: '/intel',
    element: (
      <Shell>
        <KnowledgeGraphView />
      </Shell>
    ),
  },
  {
    path: '/compliance',
    element: (
      <Shell>
        <ComplianceView />
      </Shell>
    ),
  },
  {
    path: '/settings',
    element: (
      <Shell>
        <SettingsView />
      </Shell>
    ),
  },
    { path: '*', element: <Navigate to="/" replace /> },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);

function GlobeFallback() {
  return (
    <div style={{ flex: 1, display: 'flex', padding: 16, gap: 16, flexDirection: 'column' }}>
      <Skeleton height={36} width={280} />
      <Skeleton height={400} />
    </div>
  );
}

export function App() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [mobileUnsupported, setMobileUnsupported] = useState(() =>
    isUnsupportedMobileDevice(),
  );
  const loadAll = useData((s) => s.loadAll);
  const loaded = useData((s) => s.loaded);
  const loadError = useData((s) => s.loadError);
  const setVessels = useData((s) => s.setVessels);
  const pushAlert = useData((s) => s.pushAlert);

  useEffect(() => {
    let mounted = true;
    void fetch('/api/session', { credentials: 'same-origin' })
      .then((response) => response.json() as Promise<{ authenticated?: boolean }>)
      .then((session) => {
        if (mounted) setAuthenticated(session.authenticated === true);
      })
      .catch(() => {
        if (mounted) setAuthenticated(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const updateMobileState = () => setMobileUnsupported(isUnsupportedMobileDevice());
    updateMobileState();
    window.addEventListener('resize', updateMobileState);
    window.addEventListener('orientationchange', updateMobileState);
    return () => {
      window.removeEventListener('resize', updateMobileState);
      window.removeEventListener('orientationchange', updateMobileState);
    };
  }, []);

  useEffect(() => {
    if (!authenticated || mobileUnsupported) return;
    void loadAll();
  }, [authenticated, mobileUnsupported, loadAll]);

  useEffect(() => {
    if (!authenticated || mobileUnsupported || !loaded) return;
    const sim = new FeedSimulator();
    sim.start(useData.getState().vessels);
    const offV = sim.onVessels((vs) => setVessels(vs));
    const offA = sim.onAlert((a) => pushAlert(a));
    return () => {
      offV();
      offA();
      sim.stop();
    };
  }, [authenticated, mobileUnsupported, loaded, setVessels, pushAlert]);

  useEffect(() => {
    const onLogout = () => setAuthenticated(false);
    window.addEventListener('iris-demo-logout', onLogout);
    return () => window.removeEventListener('iris-demo-logout', onLogout);
  }, []);

  if (authenticated === null) {
    return <LoadingScreen caption="Checking demo access" />;
  }

  if (!authenticated) {
    return <DemoLogin onAuthenticated={() => setAuthenticated(true)} />;
  }

  if (mobileUnsupported) {
    return <MobileUnsupportedScreen />;
  }

  if (loadError) {
    return (
      <div style={{ padding: 32, color: 'var(--status-critical)' }}>
        <h1>Failed to load Iris seed data</h1>
        <p>{loadError}</p>
      </div>
    );
  }
  if (!loaded) {
    return <LoadingScreen caption="Initialising platform · loading sensor fleet" />;
  }
  return (
    <RouterProvider
      router={router}
      future={{ v7_startTransition: true }}
    />
  );
}

function isUnsupportedMobileDevice() {
  if (typeof window === 'undefined') return false;
  const userAgentData = navigator as Navigator & {
    userAgentData?: { mobile?: boolean };
  };
  const reportsMobile = userAgentData.userAgentData?.mobile === true;
  const mobileUserAgent = /Android|iPhone|iPod|IEMobile|Opera Mini|Mobile/i.test(
    navigator.userAgent,
  );
  const narrowCoarsePointer = window.matchMedia(
    '(max-width: 900px) and (pointer: coarse)',
  ).matches;
  return reportsMobile || mobileUserAgent || narrowCoarsePointer;
}

function LoadingScreen({ caption }: { caption: string }) {
  return (
    <div
      style={{
        padding: 64,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'var(--bg-primary)',
      }}
    >
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: 1,
          color: 'var(--text-primary)',
        }}
      >
        IRIS
      </div>
      <div className="caption">{caption}</div>
      <Skeleton width={420} height={4} />
    </div>
  );
}

function MobileUnsupportedScreen() {
  return (
    <main className="iris-mobile-root">
      <section className="iris-mobile-panel" aria-labelledby="iris-mobile-title">
        <div className="iris-mobile-wordmark">IRIS</div>
        <h1 id="iris-mobile-title">Desktop Required</h1>
        <p>
          The Iris demo is not supported on mobile. Please open the platform on a
          desktop or laptop browser to use the full operating picture.
        </p>
      </section>
    </main>
  );
}

function DemoLogin({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  return (
    <main className="iris-login-root">
      <section className="iris-login-panel" aria-labelledby="iris-login-title">
        <div className="iris-login-wordmark">IRIS</div>
        <h1 id="iris-login-title">Demo Access</h1>
        <p>Sign in with the demo credentials provided by the Iris team.</p>
        <form
          className="iris-login-form"
          onSubmit={async (event) => {
            event.preventDefault();
            setSubmitting(true);
            setError('');
            try {
              const response = await fetch('/api/login', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, password }),
              });
              if (response.ok) {
                onAuthenticated();
                return;
              }
              const body = await response.json().catch(() => null) as { error?: string } | null;
              setError(body?.error ?? 'Invalid demo username or password.');
            } catch {
              setError('Demo access is unavailable. Please try again shortly.');
            } finally {
              setSubmitting(false);
            }
          }}
        >
          <label>
            <span>Email</span>
            <input
              autoComplete="username"
              type="email"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
          </label>
          <label>
            <span>Password</span>
            <input
              autoComplete="current-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          {error && <div className="iris-login-error">{error}</div>}
          <button type="submit" disabled={submitting}>
            {submitting ? 'Checking...' : 'Enter demo'}
          </button>
        </form>
      </section>
    </main>
  );
}
